// qs-DrawingGallery.vue
<template>
  <div
    v-if="isOpen"
    class="drawing-gallery fixed left-1 top-1 h-[calc(100vh-8px)] w-80 bg-white z-40 overflow-hidden rounded-lg"
  >
    <div class="gallery-content p-2 overflow-y-auto h-full">
      <div
        v-if="drawings.length === 0"
        class="text-center text-gray-500 py-8"
      >
        <FileText class="mx-auto mb-4 text-gray-300" />
        <p>No drawings yet</p>
        <p class="text-sm">Create your first drawing!</p>
      </div>

      <div v-else class="space-y-1">
        <div
          v-for="drawing in drawingsSorted"
          :key="drawing.id"
          class="drawing-item group relative p-2 hover:bg-gray-50 transition-all rounded"
          :class="{
            'bg-blue-50 border-l-4 border-blue-500': drawing.id === currentDrawingId,
            'bg-white': drawing.id !== currentDrawingId
          }"
        >
          <div class="flex items-center space-x-2">
            <div
              class="thumbnail-container flex-shrink-0 cursor-pointer"
              @click="selectDrawing(drawing.id)"
            >
              <div
                class="w-12 h-12 rounded-full flex items-center justify-center relative"
                :style="{
                  backgroundColor:
                    drawing.canvasState?.backgroundColor ||
                    drawing.toolState?.backgroundColor ||
                    '#f5f5dc'
                }"
              >
                <div class="w-6 h-6 border-2 border-white rounded-full opacity-60" />
                <div class="absolute w-2 h-2 bg-white rounded-full" />
              </div>
            </div>

            <div class="drawing-info flex-1 min-w-0" @click.stop>
              <div class="flex items-center space-x-2">
                <div v-if="renamingDrawing === drawing.id" class="flex-1">
                  <input
                    :ref="
                      el => {
                        if (renamingDrawing === drawing.id) {
                          renameInput = el as HTMLInputElement | null
                        }
                      }
                    "
                    v-model="newDrawingName"
                    class="w-full p-1 text-sm font-medium text-gray-900 bg-transparent border-none outline-none"
                    @blur="confirmRename"
                    @keyup.enter="confirmRename"
                    @keyup.escape="cancelRename"
                    @click.stop
                  />
                </div>
                <h3
                  v-else
                  class="font-medium text-gray-900 truncate cursor-text hover:bg-gray-100 px-1 py-0.5 rounded"
                  @click.stop="startRename(drawing.id, drawing.name)"
                >
                  {{ drawing.name }}
                </h3>
                <span
                  v-if="drawing.id === currentDrawingId"
                  class="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full"
                >
                  Current
                </span>
              </div>
              <div class="text-xs text-gray-500 ml-1 space-y-0.5">
                <p>Created: {{ formatDate(drawing.createdAt) }}</p>
                <p>Modified: {{ formatDate(drawing.updatedAt) }}</p>
              </div>
            </div>

            <div
              class="drawing-actions opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1"
            >
              <button
                class="p-1 hover:bg-red-100 rounded text-gray-600 hover:text-red-600"
                title="Delete"
                @click.stop="confirmDelete(drawing.id, drawing.name)"
              >
                <Trash2 />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="deletingDrawing"
      class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-60"
      @click="cancelDelete"
    >
      <div class="bg-white p-6 w-80" @click.stop>
        <h3 class="text-lg font-medium mb-4 text-gray-900">Delete Drawing</h3>
        <p class="text-gray-600 mb-4">
          Are you sure you want to delete "{{ deletingDrawingName }}"? This action cannot be
          undone.
        </p>
        <div class="flex justify-end space-x-2">
          <button class="px-4 py-2 text-gray-600 hover:text-gray-900" @click="cancelDelete">
            Cancel
          </button>
          <button
            class="px-4 py-2 bg-red-600 text-white hover:bg-red-700"
            @click="confirmDeleteAction"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Drawing } from '@/stores/qs-store-gallery'
import { useGalleryStore } from '@/stores/qs-store-gallery'
import { FileText, Trash2 } from 'lucide-vue-next'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'drawing-selected': [drawing: Drawing]
  'close': []
}>()

const galleryStore = useGalleryStore()

const drawings = computed(() => galleryStore.drawings)
const drawingsSorted = computed(() => galleryStore.drawingsSorted)
const currentDrawingId = computed(() => galleryStore.currentDrawingId)

const renamingDrawing = ref<string | null>(null)
const newDrawingName = ref('')
const deletingDrawing = ref<string | null>(null)
const deletingDrawingName = ref('')
const renameInput = ref<HTMLInputElement | null>(null)

const selectDrawing = (drawingId: string) => {
  const drawing = galleryStore.loadDrawing(drawingId)
  if (drawing) {
    emit('drawing-selected', drawing)
    emit('close')
  }
}

const startRename = (drawingId: string, currentName: string) => {
  renamingDrawing.value = drawingId
  newDrawingName.value = currentName
  nextTick(() => {
    const input = renameInput.value
    if (input) {
      input.focus()
      input.select()
    }
  })
}

const confirmRename = () => {
  if (renamingDrawing.value && newDrawingName.value.trim()) {
    galleryStore.renameDrawing(renamingDrawing.value, newDrawingName.value.trim())
    renamingDrawing.value = null
    newDrawingName.value = ''
  }
}

const cancelRename = () => {
  renamingDrawing.value = null
  newDrawingName.value = ''
}

const confirmDelete = (drawingId: string, drawingName: string) => {
  deletingDrawing.value = drawingId
  deletingDrawingName.value = drawingName
}

const confirmDeleteAction = () => {
  if (deletingDrawing.value) {
    galleryStore.deleteDrawing(deletingDrawing.value)
    deletingDrawing.value = null
    deletingDrawingName.value = ''
  }
}

const cancelDelete = () => {
  deletingDrawing.value = null
  deletingDrawingName.value = ''
}

const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diff = now.getTime() - dateObj.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (days === 0) {
    return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  if (days === 1) {
    return 'Yesterday'
  }
  if (days < 7) {
    return `${days}d ago`
  }
  if (days < 365) {
    return dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }
  return dateObj.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
}

const handleClose = () => {
  if (props.isOpen) {
    emit('close')
  }
}

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.isOpen) {
    emit('close')
  }
}

onMounted(() => {
  window.addEventListener('drawing-completed', handleClose)
  window.addEventListener('layers-changed', handleClose)
  window.addEventListener('tool-state-refresh', handleClose)
  window.addEventListener('panel-content-changed', handleClose)
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('drawing-completed', handleClose)
  window.removeEventListener('layers-changed', handleClose)
  window.removeEventListener('tool-state-refresh', handleClose)
  window.removeEventListener('panel-content-changed', handleClose)
  window.removeEventListener('keydown', handleKeyDown)
})
</script>

<style scoped>
.gallery-content {
  height: 100%;
}

.drawing-item {
  transition: all 0.15s ease;
}
</style>